/**
 * Hook para gerenciar presença online do agente
 * 
 * Funcionalidades:
 * - Marca agente como 'online' ao montar
 * - Marca como 'offline' ao desmontar
 * - Heartbeat a cada 30s para manter status atualizado
 * - Expõe função para mudar status manualmente
 * 
 * @module useAgentPresence
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para este módulo
const logger = createLogger('useAgentPresence');

// Tipos de status online disponíveis
export type OnlineStatus = 'online' | 'away' | 'busy' | 'offline';

// Interface do retorno do hook
interface UseAgentPresenceReturn {
  /** Status online atual do agente */
  onlineStatus: OnlineStatus;
  /** Se está disponível para receber distribuição automática */
  availableForDistribution: boolean;
  /** Mensagem de status personalizada */
  statusMessage: string | null;
  /** Indica se está atualizando status */
  updating: boolean;
  /** Atualiza o status online do agente */
  updateStatus: (status: OnlineStatus, message?: string | null) => Promise<void>;
  /** Atualiza disponibilidade para distribuição */
  setAvailable: (available: boolean) => Promise<void>;
}

// Intervalo de heartbeat em milissegundos (60s — reduz pela metade o volume
// de RPCs de presença sem impactar a UX de "online" nem a distribuição.)
const HEARTBEAT_INTERVAL = 60000;

/**
 * Hook para gerenciar presença online do agente no sistema
 * 
 * @returns Objeto com status atual e funções de controle
 */
export function useAgentPresence(): UseAgentPresenceReturn {
  const { user, currentTenant } = useAuth();
  
  // Estados locais
  const [onlineStatus, setOnlineStatus] = useState<OnlineStatus>('offline');
  const [availableForDistribution, setAvailableForDistribution] = useState(true);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  
  // Ref para controlar se já inicializou
  const initializedRef = useRef(false);
  // Ref para o intervalo de heartbeat
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Atualiza o status no banco de dados via função SQL
   */
  const updateStatusInDb = useCallback(async (
    status: OnlineStatus,
    available?: boolean,
    message?: string | null
  ) => {
    if (!user?.id || !currentTenant?.id) return;

    try {
      // Chama função SQL que usa auth.uid() internamente
      const { error } = await supabase.rpc('update_agent_online_status', {
        p_online_status: status,
        p_status_message: message ?? null,
        p_available_for_distribution: available ?? availableForDistribution,
      });

      if (error) {
        logger.error('Erro ao atualizar status no banco', error);
        throw error;
      }

      logger.debug('Status atualizado no banco', { status, available, message });
    } catch (error) {
      logger.error('Falha ao atualizar status', error);
      throw error;
    }
  }, [user?.id, currentTenant?.id, availableForDistribution]);

  /**
   * Atualiza o status online do agente
   */
  const updateStatus = useCallback(async (
    status: OnlineStatus,
    message?: string | null
  ) => {
    setUpdating(true);
    try {
      await updateStatusInDb(status, undefined, message);
      setOnlineStatus(status);
      if (message !== undefined) {
        setStatusMessage(message);
      }
      logger.info('Status atualizado', { status, message });
    } catch (error) {
      logger.error('Erro ao atualizar status', error);
    } finally {
      setUpdating(false);
    }
  }, [updateStatusInDb]);

  /**
   * Atualiza disponibilidade para distribuição automática
   */
  const setAvailable = useCallback(async (available: boolean) => {
    setUpdating(true);
    try {
      await updateStatusInDb(onlineStatus, available);
      setAvailableForDistribution(available);
      logger.info('Disponibilidade atualizada', { available });
    } catch (error) {
      logger.error('Erro ao atualizar disponibilidade', error);
    } finally {
      setUpdating(false);
    }
  }, [updateStatusInDb, onlineStatus]);

  /**
   * Heartbeat para manter status atualizado
   * Apenas atualiza last_seen_at sem mudar status
   */
  const sendHeartbeat = useCallback(async () => {
    if (!user?.id || !currentTenant?.id || onlineStatus === 'offline') return;

    try {
      // Atualiza last_seen_at via RPC segura (substitui PATCH direto em tenant_members)
      // A função valida auth.uid() + tenant ativo e registra tentativas inválidas
      const { error } = await supabase.rpc('update_my_presence_heartbeat', {
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro no heartbeat', error);
      } else {
        logger.debug('Heartbeat enviado');
      }
    } catch (error) {
      logger.error('Falha no heartbeat', error);
    }
  }, [user?.id, currentTenant?.id, onlineStatus]);

  /**
   * Efeito de inicialização: marca como online ao montar
   */
  useEffect(() => {
    if (!user?.id || !currentTenant?.id || initializedRef.current) return;

    const initialize = async () => {
      initializedRef.current = true;
      logger.info('Inicializando presença do agente');
      
      try {
        await updateStatusInDb('online', true);
        setOnlineStatus('online');
        setAvailableForDistribution(true);
        logger.info('Agente marcado como online');
      } catch (error) {
        logger.error('Erro ao inicializar presença', error);
      }
    };

    initialize();
  }, [user?.id, currentTenant?.id, updateStatusInDb]);

  /**
   * Efeito de cleanup: marca como offline ao desmontar
   * Utiliza o cliente autenticado do Supabase para garantir
   * que o JWT correto seja enviado, evitando erro 400 causado
   * por requisição anônima em RPC SECURITY DEFINER.
   */
  useEffect(() => {
    return () => {
      if (!user?.id || !currentTenant?.id || !initializedRef.current) return;

      logger.info('Marcando agente como offline (cleanup)');

      // Usa supabase.rpc com cliente autenticado (mesmo padrão de updateStatusInDb)
      supabase
        .rpc('update_agent_online_status', {
          p_online_status: 'offline',
          p_status_message: null,
          p_available_for_distribution: false,
        })
        .then(({ error }) => {
          if (error) {
            logger.error('Erro ao marcar offline no cleanup', error);
          }
        });
    };
  }, [user?.id, currentTenant?.id]);

  /**
   * Efeito do heartbeat: envia a cada 30 segundos
   */
  useEffect(() => {
    if (onlineStatus === 'offline') {
      // Limpa intervalo se offline
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    // Inicia heartbeat
    heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
    logger.debug('Heartbeat iniciado');

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
        logger.debug('Heartbeat parado');
      }
    };
  }, [onlineStatus, sendHeartbeat]);

  /**
   * Listener para evento beforeunload
   * Marca como offline quando usuário fecha aba/navegador
   */
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!user?.id || !currentTenant?.id) return;

      // Usa sendBeacon para envio assíncrono garantido
      // Payload com apenas parâmetros aceitos pela função SQL
      const payload = JSON.stringify({
        p_online_status: 'offline',
        p_status_message: null,
        p_available_for_distribution: false,
      });

      navigator.sendBeacon?.(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/update_agent_online_status`,
        new Blob([payload], { type: 'application/json' })
      );
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [user?.id, currentTenant?.id]);

  return {
    onlineStatus,
    availableForDistribution,
    statusMessage,
    updating,
    updateStatus,
    setAvailable,
  };
}
